import { AlertCircle, Loader2, X } from 'lucide-react';

import { type useJudgeVerdicts } from '../../hooks/useJudgeVerdicts';
import { type JudgeOutcome, type JudgeRootCause } from '../../services/api/judge';
import { getApiErrorDetails } from '../../utils/api-error';
import { cn } from '../../utils/cn';
import { formatDateTime } from '../../utils/datetime';

import {
  intentLabel,
  OUTCOME_LABELS,
  outcomeBadgeColor,
  outcomeLabel,
  ROOT_CAUSE_LABELS,
  rootCauseLabel,
} from './kvalitet-labels';
import { isBadSelection } from './kvalitet-url-state';
import { KvalitetSessionLink } from './KvalitetSessionLink';

interface KvalitetSessionTableProps {
  list: ReturnType<typeof useJudgeVerdicts>;
  outcomes: readonly JudgeOutcome[];
  rootCause: JudgeRootCause | null;
  onClearOutcomes: () => void;
  onClearRootCause: () => void;
}

const ActiveFilter = ({ label, onClear }: { label: string; onClear: () => void }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
    {label}
    <button
      type="button"
      onClick={onClear}
      aria-label={`Ta bort filtret ${label}`}
      className="rounded-full p-0.5 hover:bg-blue-100"
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

export const KvalitetSessionTable = ({
  list,
  outcomes,
  rootCause,
  onClearOutcomes,
  onClearRootCause,
}: KvalitetSessionTableProps) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Sessioner</h3>
      {list.total !== null && <span className="text-xs text-slate-400">({list.total} st)</span>}
      {outcomes.length > 0 && (
        <ActiveFilter
          label={
            isBadSelection(outcomes)
              ? 'Dåliga utfall'
              : outcomes.map((outcome) => OUTCOME_LABELS[outcome]).join(', ')
          }
          onClear={onClearOutcomes}
        />
      )}
      {rootCause && (
        <ActiveFilter label={ROOT_CAUSE_LABELS[rootCause]} onClear={onClearRootCause} />
      )}
    </div>

    {list.isLoading ? (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
      </div>
    ) : list.error ? (
      <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <span>Kunde inte ladda domarna: {getApiErrorDetails(list.error)}</span>
      </div>
    ) : list.items.length === 0 ? (
      <div className="p-8 text-center rounded-lg border border-slate-200 bg-slate-50">
        <p className="text-slate-600">Inga bedömda sessioner med de här filtren.</p>
      </div>
    ) : (
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-brand-beige-tint-1">
            <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
              <th className="px-3 py-2">Session</th>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Ärende</th>
              <th className="px-3 py-2">Mål</th>
              <th className="px-3 py-2">Utfall</th>
              <th className="px-3 py-2">Rotorsak</th>
              <th className="px-3 py-2">Citat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white align-top">
            {list.items.map((item) => (
              <tr key={item.sessionId}>
                <td className="px-3 py-2">
                  <KvalitetSessionLink sessionId={item.sessionId} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {formatDateTime(item.sourceCreatedAt)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {intentLabel(item.intent)}
                </td>
                <td className="px-3 py-2 text-slate-900 min-w-[12rem]">
                  {item.customerGoal ?? '–'}
                  {item.missingForBot && (
                    <div className="mt-1 text-xs text-slate-500">
                      Saknades: {item.missingForBot}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      'inline-block whitespace-nowrap rounded-sm border px-2 py-0.5 text-xs font-medium',
                      outcomeBadgeColor(item.outcome),
                    )}
                  >
                    {outcomeLabel(item.outcome)}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {rootCauseLabel(item.rootCause)}
                </td>
                <td className="px-3 py-2 text-slate-600 italic min-w-[14rem]">
                  {item.quote ? `”${item.quote}”` : '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {list.hasMore && (
      <div className="flex justify-center">
        <button
          type="button"
          disabled={list.isFetchingMore}
          onClick={list.loadMore}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {list.isFetchingMore ? 'Laddar…' : 'Ladda fler'}
        </button>
      </div>
    )}
  </div>
);
